/**
 * Cálculo do "Quanto Sobra de Verdade": dado o valor bruto e o canal/modalidade,
 * retorna o líquido real, o prazo de recebimento e o custo de antecipar.
 *
 * Funções puras (sem Date) — o componente calcula as datas reais a partir dos `dias`.
 * Aproximações estão documentadas; o objetivo é clareza de decisão, não exatidão contábil.
 */

import type { Maquininha, Marketplace, ModalidadePagamento } from './rates'

export interface Recebimento {
  tipo: 'na_hora' | 'dias' | 'parcelado'
  /** Dias até a 1ª parcela (ou até o valor único). */
  diasPrimeira: number
  /** Dias até a última parcela cair. */
  diasUltima: number
  parcelas: number
  /** Valor líquido de cada parcela. */
  valorParcela: number
}

export interface Antecipacao {
  /** Custo em R$ para receber tudo em ~1 dia. */
  custoReais: number
  /** Líquido na conta se antecipar. */
  liquido: number
  diasComAntecipacao: number
  /** Custo da antecipação como % do líquido (pra dar a real do "juro disfarçado"). */
  custoPct: number
}

export interface ResultadoLiquido {
  bruto: number
  /** % total descontado do canal. */
  taxaPct: number
  taxaReais: number
  /** Líquido sem antecipar. */
  liquido: number
  recebimento: Recebimento
  /** Presente só quando faz sentido antecipar (crédito com custo de antecipação). */
  antecipacao: Antecipacao | null
}

function taxaCreditoParcelado(m: Maquininha, parcelas: number): number {
  return parcelas <= 6 ? m.credito_2a6 : m.credito_7a12
}

export interface ParamsMaquininha {
  maquininha: Maquininha
  valor: number
  modalidade: ModalidadePagamento
  /** Nº de parcelas quando modalidade = credito_parcelado. */
  parcelas?: number
}

export function calcMaquininha({
  maquininha,
  valor,
  modalidade,
  parcelas = 1,
}: ParamsMaquininha): ResultadoLiquido {
  let taxaPct: number
  let nParcelas = 1

  switch (modalidade) {
    case 'pix':
      taxaPct = maquininha.pix
      break
    case 'debito':
      taxaPct = maquininha.debito
      break
    case 'credito_avista':
      taxaPct = maquininha.credito_avista
      break
    case 'credito_parcelado':
      nParcelas = Math.max(2, parcelas)
      taxaPct = taxaCreditoParcelado(maquininha, nParcelas)
      break
  }

  const taxaReais = round2((valor * taxaPct) / 100)
  const liquido = round2(valor - taxaReais)

  // Prazo de recebimento
  let recebimento: Recebimento
  if (modalidade === 'pix') {
    recebimento = { tipo: 'na_hora', diasPrimeira: 1, diasUltima: 1, parcelas: 1, valorParcela: liquido }
  } else if (modalidade === 'debito') {
    recebimento = { tipo: 'dias', diasPrimeira: 1, diasUltima: 1, parcelas: 1, valorParcela: liquido }
  } else if (modalidade === 'credito_avista') {
    const dias = maquininha.recebimentoCreditoDias
    recebimento = {
      tipo: dias <= 1 ? 'na_hora' : 'dias',
      diasPrimeira: dias,
      diasUltima: dias,
      parcelas: 1,
      valorParcela: liquido,
    }
  } else {
    const base = maquininha.recebimentoCreditoDias
    const diasUltima = base + 30 * (nParcelas - 1)
    recebimento = {
      tipo: 'parcelado',
      diasPrimeira: base,
      diasUltima,
      parcelas: nParcelas,
      valorParcela: round2(liquido / nParcelas),
    }
  }

  // Antecipação (só crédito com custo > 0 e prazo > 1 dia)
  let antecipacao: Antecipacao | null = null
  const ehCredito = modalidade === 'credito_avista' || modalidade === 'credito_parcelado'
  if (ehCredito && maquininha.antecipacaoMensalPct > 0 && recebimento.diasPrimeira > 1) {
    // Meses médios adiantados: à vista = dias/30; parcelado = dias1/30 + (N-1)/2
    const mesesMedios =
      modalidade === 'credito_parcelado'
        ? maquininha.recebimentoCreditoDias / 30 + (nParcelas - 1) / 2
        : maquininha.recebimentoCreditoDias / 30
    const custoReais = round2(liquido * (maquininha.antecipacaoMensalPct / 100) * mesesMedios)
    antecipacao = {
      custoReais,
      liquido: round2(liquido - custoReais),
      diasComAntecipacao: 1,
      custoPct: liquido > 0 ? round2((custoReais / liquido) * 100) : 0,
    }
  }

  return { bruto: valor, taxaPct, taxaReais, liquido, recebimento, antecipacao }
}

export interface ParamsMarketplace {
  marketplace: Marketplace
  valor: number
}

export function calcMarketplace({ marketplace, valor }: ParamsMarketplace): ResultadoLiquido {
  const taxaPctVar = marketplace.comissaoPct + marketplace.taxaTransacaoPct
  const taxaReais = round2((valor * taxaPctVar) / 100 + marketplace.taxaFixaReais)
  const liquido = round2(valor - taxaReais)
  // taxa efetiva incluindo a fixa (pra exibir um % honesto)
  const taxaPct = valor > 0 ? round2((taxaReais / valor) * 100) : taxaPctVar

  return {
    bruto: valor,
    taxaPct,
    taxaReais,
    liquido,
    recebimento: {
      tipo: marketplace.recebimentoDias <= 1 ? 'na_hora' : 'dias',
      diasPrimeira: marketplace.recebimentoDias,
      diasUltima: marketplace.recebimentoDias,
      parcelas: 1,
      valorParcela: liquido,
    },
    antecipacao: null,
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}
